import { COLORS, DSBottomSheet } from '@repo/design-system/index'
import {
  QIcon_check_mark,
  QIcon_chevron_bold_right,
  QIcon_chevron_down,
  QIcon_chevron_left,
  QIcon_chevron_up,
  QIcon_close_default,
  QIcon_tune,
} from '@repo/icons/index'
import { ActivityComponentType } from '@stackflow/react'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import {
  getThreadDetail,
  useConversationSetting,
  useConversationSettingSetDifficulty,
  useConversationSettingSetSpeed,
} from '../../../modules/apis/student/conversation'
import { useCurrentUser } from '../../../modules/context/UserContext'
import { APP_SCREEN_NAME } from '../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useWakeLock from '../../../modules/hooks/common/useWakeLock'
import useReturnFullUrl from '../../../modules/hooks/webview/common/useReturnFullUrl'
import { n_ai_speaking, n_ai_tutor } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import urls from '../../../modules/urls'
import { CONVERSATION_AI_LIMIT, DIFFICULTY_OPTIONS, THREAD_STATUS, speedsArray } from '../../../modules/vars'
import { useConversationAIStack } from '../../../pages/[lang]/webview/conversational-ai'
import LoadingSpinner from '../../common/LoadingSpinner'
import { ConversationCompletePopup, ConversationStopPopup } from '../../new-portal/conversational-ai/ConversationPopup'
import WebviewStackContainer from '../common/WebviewStackContainer'

const ConversationMainBody = dynamic(() => import('../../new-portal/conversational-ai/main/index'), {
  loading: () => (
    <div className="flex h-screen w-screen items-center justify-center">
      <LoadingSpinner lg show primary />
    </div>
  ),
})

const ProgressBar = ({ progress, showProgressDetail, setShowProgressDetail }) => {
  const calculatePercentage = () => {
    if (progress >= 1) return 100
    return progress * 100
  }
  const percentage = calculatePercentage()
  return (
    <div className="w-full flex items-center px-[16px] py-[4px]">
      <h4 className="w-full bg-gray-200 rounded-full overflow-hidden mr-[8px]">
        <div className="h-8 bg-purple-500" style={{ width: `${percentage}%` }} />
      </h4>
      {showProgressDetail ? (
        <QIcon_chevron_up onClick={() => setShowProgressDetail((r) => !r)} />
      ) : (
        <QIcon_chevron_down onClick={() => setShowProgressDetail((r) => !r)} />
      )}
    </div>
  )
}

const ConversationProgress = ({ turnCnt, isConversationQualified, wordCnt, minimumWord }) => {
  const { t } = useTranslation()
  const [showProgressDetail, setShowProgressDetail] = useState(false)
  return (
    <>
      <div className="flex flex-col items-center w-full z-100 bg-white bg-opacity-95 absolute">
        <ProgressBar
          progress={Math.min(wordCnt / minimumWord)}
          showProgressDetail={showProgressDetail}
          setShowProgressDetail={setShowProgressDetail}
        />
        {showProgressDetail && (
          <div className="items-center w-full z-100 flex item-center justify-center px-[12px] py-[4px]">
            <span className="Body2-14Md text-gray-500">{t(n_ai_speaking.b2b_wordcount.progress_txt1)}</span>
            <span className="bg-gray-200 w-1 h-[16px] inline-block mx-4" />
            <span className="Body2-14Md text-gray-700">{turnCnt}</span>
            <span className="Body2-14Md text-gray-400">/</span>
            <span className="Body2-14Md text-gray-400">
              {CONVERSATION_AI_LIMIT} {t(n_ai_speaking.b2b_wordcount.progress_txt2)}
            </span>
            {minimumWord ? (
              <>
                <span className="Body2-14Md text-gray-700 ml-[4px]">{wordCnt}</span>
                <span className="Body2-14Md text-gray-400">/</span>
                <span className="Body2-14Md text-gray-400">
                  {minimumWord} {t(n_ai_speaking.b2b_wordcount.wds)}
                </span>
              </>
            ) : null}
          </div>
        )}
      </div>
      {showProgressDetail && <div className="h-[20px]" />}
    </>
  )
}

const ConversationSettingBottomSheet = ({ showSetting, setShowSetting, goal }) => {
  const { t } = useTranslation()
  const [showSpeed, setShowSpeed] = useState(false)
  const [showDiffculty, setShowDiffculty] = useState(false)

  const setting = useConversationSetting()
  const setSpeed = useConversationSettingSetSpeed()
  const setDifficulty = useConversationSettingSetDifficulty()

  const onClickSpeedBtn = (speed) => {
    if (speed === setting?.data?.speed) return
    setSpeed.mutateAsync(speed).then(() => {
      setShowSpeed(false)
    })
  }

  const onClickDifficulty = (difficulty) => {
    if (difficulty === setting?.data?.difficulty) return
    setDifficulty.mutateAsync(difficulty).then(() => {
      setShowDiffculty(false)
    })
  }

  const getSettingTitle = () => {
    if (showSpeed) return t(n_ai_tutor.roleplay_setting.category1)
    return t(n_ai_speaking.ai_session.setting_txt15)
  }

  const onCloseBottomSheet = () => {
    setShowSetting(false)
    setShowDiffculty(false)
    setShowSpeed(false)
  }

  useWakeLock()

  return (
    <DSBottomSheet
      id="ConversationSettingBottomSheet"
      isOpen={showSetting}
      setIsOpen={setShowSetting}
      closeButton={false}
      closeOnDim={false}
    >
      <div className="px-[20px] mb-[64px]">
        <div className="flex flex-row justify-between items-center mb-[20px] mt-[10px]">
          <div className="Title2-18Bd ">{getSettingTitle()}</div>
          <div>
            <QIcon_close_default color={COLORS.GRAY500} width={24} height={24} onClick={() => onCloseBottomSheet()} />
          </div>
        </div>
        {showSpeed && <div className="Body2-14Md text-gray-600">{t(n_ai_tutor.roleplay_setting.detail)}</div>}
        {showDiffculty && <div className="Body2-14Md text-gray-600">{t(n_ai_tutor.discussion_setting.detail4)}</div>}

        {!showSpeed && !showDiffculty && (
          <div className="flex flex-col gap-[15px]">
            <div className="my-[15px] flex flex-row items-center justify-between Body1-16Md">
              <div>{t(n_ai_tutor.discussion_setting.category2)}</div>
              <div className="flex flex-row items-center gap-[2px] text-gray-300">
                <div>{goal}</div>
                <QIcon_chevron_bold_right color={COLORS.GRAY300} width={16} height={16} />
              </div>
            </div>
            <div
              className="my-[15px] flex flex-row items-center justify-between Body1-16Md"
              onClick={() => setShowDiffculty(true)}
            >
              <div>{t(n_ai_speaking.ai_session.setting_txt1)}</div>
              <div className="flex flex-row items-center gap-[2px] text-gray-600">
                <div>{DIFFICULTY_OPTIONS[setting?.data?.difficulty]?.label}</div>
                <QIcon_chevron_bold_right color={COLORS.GRAY500} width={16} height={16} />
              </div>
            </div>
            <div
              className="my-[15px] flex flex-row items-center justify-between Body1-16Md"
              onClick={() => setShowSpeed(true)}
            >
              <div>{t(n_ai_tutor.roleplay_setting.category1)}</div>
              <div className="flex flex-row items-center gap-[2px] text-gray-600">
                <div>{setting?.data?.speed?.toFixed(1)}</div>
                <QIcon_chevron_bold_right color={COLORS.GRAY500} width={16} height={16} />
              </div>
            </div>
          </div>
        )}

        {showSpeed && (
          <div className="flex flex-col mt-[15px]">
            {speedsArray.map((speed, index) => {
              return (
                <div
                  className="flex flex-row items-center justify-between my-[15px] Body1-16Md"
                  key={index}
                  onClick={() => onClickSpeedBtn(speed)}
                >
                  <div>{speed?.toFixed(1)}</div>
                  <div>
                    {speed == setting.data.speed && <QIcon_check_mark width={24} height={24} color={COLORS.BLACK} />}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {showDiffculty && (
          <div className="flex flex-col mt-[15px]">
            {DIFFICULTY_OPTIONS.map((difficulty, index) => {
              return (
                <div
                  className="flex flex-row items-center justify-between my-[15px] Body1-16Md"
                  key={index}
                  onClick={() => onClickDifficulty(difficulty.id)}
                >
                  <div>{difficulty.label}</div>
                  <div>
                    {difficulty.id == setting.data.difficulty && (
                      <QIcon_check_mark width={24} height={24} color={COLORS.BLACK} />
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </DSBottomSheet>
  )
}

const ConversationHeader = ({
  thread,
  title,
  isConversationDone,
  isConversationQualified,
  onClickThreadSummary,
  showCompleteModal,
  setShowCompleteModal,
}) => {
  const [stopModal, setStopModal] = useState(false)
  const getFullUrl = useReturnFullUrl()
  const { useConversationAIFlow } = useConversationAIStack()
  const { push } = useConversationAIFlow()
  const router = useAppRouter({ stackPush: push, browserDetect: true })
  const [showSetting, setShowSetting] = useState(false)

  const routerMoveListPage = () => {
    // 전화 기록은 RN(AI 전화 -> 전화 내역)에서만 진입 가능함. 이 경우 AI 튜터 기록으로 이동하지 않고 뒤로가기하여 전화 내역으로 이동
    if (thread.assistant_type === 'debate_voip') {
      router.back()
    } else {
      router.push(urls.webview.conversationalAI.list, {
        appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
        appSendData: { url: getFullUrl(urls.webview.conversationalAI.list) },
      })
    }
  }

  const onClickExitBtn = () => {
    console.log('onClickExitBtn', isConversationDone, isConversationQualified)
    if (isConversationQualified) {
      // 세션 마무리되었으면 setShowCompleteModal
      setShowCompleteModal(true)
    } else {
      // 세션 마무리 안되었으면 setStopModal
      setStopModal(true)
    }
  }

  return (
    <div className="flex w-screen flex-row items-center justify-between py-[12px] px-[20px]">
      <div>
        <QIcon_chevron_left
          width={24}
          height={24}
          color={COLORS.BLACK}
          onClick={() => {
            if (isConversationDone) {
              routerMoveListPage()
            } else {
              onClickExitBtn()
            }
          }}
        />
      </div>
      <div className="flex-1 flex flex-row items-center justify-center min-w-[0]">
        {title ? (
          <div className="h-full gap-x-[20px] Body1-16Md w-full truncate">{title}</div>
        ) : (
          <div className="h-full gap-x-[20px] Body1-16M animate-pulse rounded-md bg-gray-100 text-transparent w-full truncate">
            Loading
          </div>
        )}
      </div>
      <div>
        <QIcon_tune width={24} height={24} color={COLORS.BLACK} onClick={() => setShowSetting((prev) => !prev)} />
      </div>

      <ConversationSettingBottomSheet showSetting={showSetting} setShowSetting={setShowSetting} goal={thread?.goal} />

      <ConversationStopPopup
        thread={thread}
        stopModal={stopModal}
        setStopModal={setStopModal}
        minimumWord={thread?.minimum_word}
      />
      <ConversationCompletePopup
        showCompleteModal={showCompleteModal}
        setShowCompleteModal={setShowCompleteModal}
        onClickThreadSummary={onClickThreadSummary}
      />
    </div>
  )
}

const ConversationMainActivity: ActivityComponentType = () => {
  const router = useAppRouter()
  const threadId = router.query?.threadId
  const readonly = router.query?.readonly === 'true'

  const { currentUser } = useCurrentUser()

  const [thread, setThread] = useState(null)
  const [showCompleteModal, setShowCompleteModal] = useState(false)
  const [isConversationQualified, setIsConversationQualified] = useState(false)
  const [triggerSummary, setTriggerSummary] = useState(false)
  const [turnCnt, setTurnCnt] = useState(0)
  const [wordCnt, setWordCnt] = useState(0)
  const [isSessionDone, setIsSessionDone] = useState(false)

  useEffect(() => {
    if (threadId) {
      getThreadDetail(threadId).then(setThread)
    }
  }, [threadId])

  const onClickThreadSummary = () => {
    setTriggerSummary(true)
    setShowCompleteModal(false)
  }

  if (!currentUser) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <LoadingSpinner lg show primary />
      </div>
    )
  }

  return (
    <WebviewStackContainer
      height="52px"
      border={false}
      title={
        <div className="w-full flex flex-col">
          <ConversationHeader
            thread={thread}
            title={thread?.title || ''}
            isConversationDone={thread?.status !== THREAD_STATUS.IN_PROGRESS || isSessionDone}
            isConversationQualified={isConversationQualified}
            onClickThreadSummary={onClickThreadSummary}
            showCompleteModal={showCompleteModal}
            setShowCompleteModal={setShowCompleteModal}
          />
        </div>
      }
    >
      <ConversationProgress
        turnCnt={turnCnt}
        wordCnt={wordCnt}
        isConversationQualified={isConversationQualified}
        minimumWord={thread?.minimum_word}
        // minimumWord={50}
      />
      <ConversationMainBody
        thread={thread}
        threadId={threadId}
        readonly={readonly}
        setTurnCnt={setTurnCnt}
        setWordCnt={setWordCnt}
        setIsConversationQualified={setIsConversationQualified}
        triggerSummary={triggerSummary}
        setTriggerSummary={setTriggerSummary}
        isSessionDone={isSessionDone}
        setIsSessionDone={setIsSessionDone}
      />
    </WebviewStackContainer>
  )
}

export default ConversationMainActivity
